import { useState } from "react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs"
import { BATCHES, batchValue } from "./batches"
import BatchDetail from "./components/BatchDetail"
import type { Mention } from "./components/MentionedBy"
import ProductDetail from "./components/ProductDetail"
import ProductList, { type AgendaRow } from "./components/ProductList"
import { CATEGORIES, PRODUCTS, type PushAction, type PushProduct, STARTING_ACTIONS, listName, monthsLabel, num, valueOf } from "./data"
import { formatDate } from "./dates"

// Both parts list the most valuable stock first.
const EXPIRING = [...BATCHES].sort((a, b) => batchValue(b) - batchValue(a))
const highStockIn = (category: string) =>
  PRODUCTS.filter((p) => p.highDoc && p.category === category).sort((a, b) => valueOf(b) - valueOf(a))


// The code under the name, unless the short name already shows it.
const idLine = (p: PushProduct) => {
  const id = p.code ?? p.sku
  return id === listName(p) ? [] : [id]
}

const batchRows: AgendaRow[] = EXPIRING.map((b) => ({
  id: b.id,
  name: listName(b.product),
  subtitle: [...idLine(b.product), formatDate(b.expires, { month: "short", day: "numeric", year: "numeric" })],
  qty: b.qty,
  value: batchValue(b),
  keyValue: monthsLabel(b.monthsLeft),
}))

const productRows = (products: PushProduct[]): AgendaRow[] =>
  products.map((p) => ({
    id: p.id,
    name: listName(p),
    subtitle: idLine(p),
    qty: p.stock,
    value: valueOf(p),
    keyValue: p.coverDays === null ? "No movement" : num(p.coverDays),
  }))

// The push review on one shared screen, one row at a time.
// Left: the agenda for this part. Right: the row being discussed.
// Part 1 goes batch by batch (a product with one expiry date) through every
// batch expiring within 18 months; part 2 goes category by category through
// the important products with too much stock.
// Actions are kept per product id ("LX8D4BE32768") or per batch id
// ("LX80C6E461D8@2027-06-20").
export default function PushReview() {
  const [actions, setActions] = useState<Record<string, PushAction[]>>(STARTING_ACTIONS)
  const [selected, setSelected] = useState<Record<string, string>>({})

  const mentionsOf = (sku: string): Mention[] =>
    Object.entries(actions).flatMap(([key, list]) => {
      const fromSku = key.split("@")[0]
      return fromSku === sku ? [] : list
        .filter((a) => new RegExp(`#${sku}(?![A-Za-z0-9-])`).test(a.note))
        .map((action) => ({ fromSku, action }))
    })
  const countOf = (id: string) => (actions[id] ?? []).length
  const update = (id: string) => (next: PushAction[]) => setActions({ ...actions, [id]: next })

  const workspace = (listKey: string, rows: AgendaRow[], keyLabel: string, detail: (id: string) => React.ReactNode) => {
    const currentId = rows.some((r) => r.id === selected[listKey]) ? selected[listKey] : rows[0].id
    return (
      <div className="grid grid-cols-[520px_1fr] items-start gap-5">
        <ProductList
          rows={rows}
          selectedId={currentId}
          actionCount={countOf}
          keyLabel={keyLabel}
          onSelect={(id) => setSelected({ ...selected, [listKey]: id })}
        />
        {detail(currentId)}
      </div>
    )
  }

  const batchDetail = (id: string) => {
    const batch = BATCHES.find((b) => b.id === id)!
    return <BatchDetail key={id} batch={batch} actions={actions[id] ?? []} mentionedBy={mentionsOf(batch.product.sku)} onChange={update(id)} />
  }

  const productDetail = (id: string) => {
    const product = PRODUCTS.find((p) => p.id === id)!
    return <ProductDetail key={id} product={product} actions={actions[id] ?? []} mentionedBy={mentionsOf(product.sku)} onChange={update(id)} />
  }

  return (
    <AppLayout
      header={<PageTitle title="Push review" subtitle="Decide how to push each product. Expiring batches first, then high stock by category." />}
    >
      <Tabs defaultValue="expiring">
        <TabsList variant="segmented" className="mb-5">
          <TabsTrigger variant="segmented" value="expiring">1. Expiring soon</TabsTrigger>
          <TabsTrigger variant="segmented" value="high-stock">2. High DOC</TabsTrigger>
        </TabsList>

        <TabsContent value="expiring">{workspace("expiring", batchRows, "Expires in", batchDetail)}</TabsContent>

        <TabsContent value="high-stock">
          <Tabs defaultValue={CATEGORIES[0]}>
            <TabsList variant="nav" className="mb-4 w-full">
              {CATEGORIES.map((c) => (
                <TabsTrigger key={c} variant="nav" value={c} className="text-sm">
                  {c}
                </TabsTrigger>
              ))}
            </TabsList>
            {CATEGORIES.map((c) => (
              <TabsContent key={c} value={c}>{workspace(c, productRows(highStockIn(c)), "DOC", productDetail)}</TabsContent>
            ))}
          </Tabs>
        </TabsContent>
      </Tabs>
    </AppLayout>
  )
}
